import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "@sg/i18n";
import { AlertDialog, NotificationProvider, Spinner } from "@sg/ui";
import clsx from "clsx";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePrevious } from "react-use";
import { useCurrentOrgId } from "~/hooks/util";
import { useIsSuperAdmin } from "~/hooks/util/useRoles";
import { useCurrentRouteConfig } from "~/routes";
import useAuthStore from "~/store/auth";
import useSettingPrefStore from "~/store/setttingPref";
import { logoutCleaner } from "~/util/logout";
import Breadcrumbs from "./Breadcrumbs";
import { Sidebar } from "./Sidebar";

const LayoutSpinner = () => (
  <>
    <div className="h-screen flex items-center justify-center">
      <Spinner className="h-full" size="xl" />
    </div>
  </>
);

function EntityUpdateHandler() {
  const isSuperAdmin = useIsSuperAdmin();

  const params = useParams();
  const prevParams = usePrevious(params);

  const location = useLocation();
  const navigate = useNavigate();

  const entity = useSettingPrefStore((state) => state.entity);
  const setEntity = useSettingPrefStore((state) => state.setEntity);

  const orgId = useCurrentOrgId();

  // // Redirect if CP doesn't match corresponding org.
  useEffect(() => {
    if (isSuperAdmin && orgId && params.orgId && params.orgId !== orgId) {
      navigate(location.pathname.replace(params.orgId, orgId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, params.orgId, isSuperAdmin]);

  // Update entity context if we change URLs.
  useEffect(() => {
    const currCpId = params.cpId;
    if (currCpId && currCpId !== prevParams?.cpId && currCpId !== entity) {
      setEntity(currCpId);
    }
  }, [entity, params, prevParams, setEntity]);

  return <></>;
}

export function Layout() {
  const { t } = useTranslation();
  const { isLoading, isAuthenticated, logout } = useAuth0();
  const authError = useAuthStore((state) => state.authError);
  const route = useCurrentRouteConfig();
  const initialized = useAuthStore((state) => state.initialized);
  const shouldHideLayout = isLoading || route?.props?.hideLayout;

  if (!!authError)
    return (
      <AlertDialog
        open
        title={t("login:error")}
        description={`${authError}`}
        actions={{
          closeLabel: t("login:return_login"),
          onClose: () => {
            logout({ returnTo: window.location.origin });
            logoutCleaner();
          },
        }}
      />
    );

  if (isLoading || (isAuthenticated && !initialized)) return <LayoutSpinner />;

  return (
    <>
      <NotificationProvider position="top-right" />
      {isAuthenticated && <EntityUpdateHandler />}
      <div className="min-h-screen">
        <Sidebar hidden={shouldHideLayout}>
          <main>
            <div className={clsx(!shouldHideLayout && "max-w-7xl mx-auto")}>
              {!shouldHideLayout && isAuthenticated && <Breadcrumbs />}
              <Outlet />
            </div>
          </main>
        </Sidebar>
      </div>
    </>
  );
}
